'use client';
/**
 * components/PwaInstall.tsx — OGRYN
 *
 * Enregistre le service worker et propose l'installation "app" :
 *  - Android/Chrome : bouton réel qui déclenche le prompt natif (beforeinstallprompt).
 *  - iPhone/Safari  : Apple interdit le prompt → bulle expliquant le geste
 *                     Partager → « Sur l'écran d'accueil ».
 *  - Déjà installé (standalone) : rien.
 *
 * Overlay auto-porté (fixed) — ne perturbe pas la mise en page de l'écran hôte.
 */

import { useEffect, useState } from 'react';

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

export default function PwaInstall() {
  const [deferred, setDeferred] = useState<InstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Enregistre le SW (installabilité Chrome).
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }

    // Détection différée (hors corps de l'effet : évite le setState synchrone
    // qui casse la règle react-hooks/set-state-in-effect, et tout mismatch SSR).
    const t = window.setTimeout(() => {
      // Déjà en mode app → on ne propose rien.
      const standalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as unknown as { standalone?: boolean }).standalone === true;
      if (standalone) return;

      const ua = window.navigator.userAgent;
      if (/iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua)) setIsIOS(true);
    }, 0);

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as InstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('beforeinstallprompt', onPrompt);
    };
  }, []);

  if (dismissed) return null;
  if (!deferred && !isIOS) return null; // rien à proposer (desktop, ou déjà installé)

  const androidInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };

  return (
    <div className="fixed inset-x-0 top-[calc(env(safe-area-inset-top)+64px)] z-[720] flex justify-center px-4 pointer-events-none">
      <div className="pointer-events-auto flex items-center gap-2 rounded-2xl bg-white/10 backdrop-blur border border-white/15 pl-3 pr-2 py-2 shadow-lg">
        <PhoneIcon />
        <button
          type="button"
          onClick={isIOS ? () => setShowIOSHint(true) : androidInstall}
          className="text-sm font-bold text-white"
        >
          Installer l’app
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Fermer"
          className="w-6 h-6 rounded-full flex items-center justify-center text-white/50 hover:text-white/80"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>

      {/* Bulle d'instruction iPhone */}
      {showIOSHint && (
        <div className="pointer-events-auto fixed inset-0 z-[721] flex items-end bg-black/50" onClick={() => setShowIOSHint(false)}>
          <div className="w-full bg-[#383838] rounded-t-3xl p-6 pb-[calc(env(safe-area-inset-bottom)+24px)] text-white" onClick={e => e.stopPropagation()}>
            <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-white/20" />
            <h2 className="text-lg font-black">Ajouter à l’écran d’accueil</h2>
            <p className="text-sm text-white/55 mt-1">Sur iPhone, l’ajout se fait en 2 gestes depuis Safari :</p>
            <ol className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center font-bold shrink-0">1</span>
                <span>Appuie sur <ShareIcon /> <b>Partager</b>, en bas de Safari.</span>
              </li>
              <li className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center font-bold shrink-0">2</span>
                <span>Choisis <b>« Sur l’écran d’accueil »</b>.</span>
              </li>
            </ol>
            <button type="button" onClick={() => setShowIOSHint(false)}
              className="mt-6 w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3.5 rounded-2xl">
              J’ai compris
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PhoneIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4FA578" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2.5" /><line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}
function ShareIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4FA578" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline align-text-bottom">
      <path d="M12 15V3" /><path d="m7 8 5-5 5 5" /><path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7" />
    </svg>
  );
}
